/**
 * Which invoices Billing & Subscription leaves out of its history (FNF-1325).
 *
 * Stripe invoices a subscription's first period the moment it is created
 * (billing_reason `subscription_create`). On per-order plans and promo trials
 * that invoice is $0 and carries nothing — usage is billed when the period
 * ends — so it only reads as a charge the restaurant didn't expect. It stays in
 * the ledger; it just isn't listed.
 */

export interface InvoiceLike {
  billingReason?: string | null;
  amountDueCents?: number | null;
  amountPaidCents?: number | null;
  periodStart?: number | null;
}

/** Records stored before billingReason existed: an opening invoice starts with the subscription. */
const OPENING_WINDOW_SECONDS = 60;

export function isZeroOpeningInvoice(
  invoice: InvoiceLike,
  subscriptionCreatedAt?: number | null,
): boolean {
  if ((invoice.amountDueCents ?? 0) !== 0 || (invoice.amountPaidCents ?? 0) !== 0) {
    return false;
  }
  if (invoice.billingReason) {
    return invoice.billingReason === 'subscription_create';
  }
  return (
    subscriptionCreatedAt != null &&
    invoice.periodStart != null &&
    Math.abs(invoice.periodStart - subscriptionCreatedAt) <= OPENING_WINDOW_SECONDS
  );
}

/**
 * An add-on billed after the month already names its quantity, "KDS × 2 · Oct 5–31"
 * (FNF-1507), so a line shouldn't add "x2" again.
 */
export function hasQuantityInLabel(description?: string | null): boolean {
  return /\s×\s?\d/.test(description || '');
}
